import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Plus, Trash2, X, AlertTriangle, Sparkles, Bell, Clock, MapPin, Loader2, LogIn, LogOut } from "lucide-react";
import { useTasks } from "../contexts/TaskContext";
import { WORKSPACES, getWorkspace } from "../lib/workspaces";
import { normaliseRecurrence, legacyFromStructured } from "../lib/recurrence";
import { cn } from "../lib/utils";
import RepeatPicker from "./RepeatPicker";
import TaskAttachments, { flushPendingAttachments } from "./TaskAttachments";
import SchedulePreview from "./SchedulePreview";
import VoiceCapture from "./VoiceCapture";
import PlaceAutocomplete from "./PlaceAutocomplete";
import SectionToggle from "./SectionToggle";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { emitUpgradeRequired } from "./UpgradeRequiredDialog";

const PRIORITIES = ["low", "normal", "important", "urgent"];
const STATUSES = [
  { v: "todo", l: "To Do" },
  { v: "inprogress", l: "In Progress" },
  { v: "done", l: "Done" },
];
const DASHBOARDS = Object.values(WORKSPACES).map((w) => ({
  v: w.key,
  l: w.fullLabel,
}));

const empty = (defaultDashboard = "personal") => ({
  title: "",
  description: "",
  priority: "normal",
  status: "todo",
  dashboard: defaultDashboard,
  due_date: "",
  due_time: "",
  due_times: [],
  start_time: "",
  end_time: "",
  location: null,
  recurring: "none",
  recurrence: null,
  subtasks: [],
  tags: [],
  notes: "",
});

export default function TaskModal({ open, onClose, task, defaultDashboard = "personal", defaultValues = null }) {
  const { createTask, updateTask, deleteTask } = useTasks();
  const [form, setForm] = useState(empty(defaultDashboard));
  const [originalDashboard, setOriginalDashboard] = useState(defaultDashboard);
  const [tagInput, setTagInput] = useState("");
  const [subInput, setSubInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [smartInput, setSmartInput] = useState("");
  const [smartParsing, setSmartParsing] = useState(false);
  const pendingAttachmentsRef = useRef([]);

  useEffect(() => {
    if (open) {
      if (task) {
        const dueTimes = task.due_times?.length
          ? task.due_times
          : (task.due_time ? [task.due_time] : []);
        setForm({
          title: task.title || "",
          description: task.description || "",
          priority: task.priority || "normal",
          status: task.status || "todo",
          dashboard: task.dashboard || defaultDashboard,
          due_date: task.due_date || "",
          due_time: task.due_time || "",
          due_times: dueTimes,
          start_time: task.start_time || "",
          end_time: task.end_time || "",
          location: task.location || null,
          recurring: task.recurring || "none",
          recurrence: normaliseRecurrence(task.recurrence, task.recurring),
          subtasks: task.subtasks || [],
          tags: task.tags || [],
          notes: task.notes || "",
        });
        setOriginalDashboard(task.dashboard || defaultDashboard);
      } else {
        setForm({ ...empty(defaultDashboard), ...(defaultValues || {}) });
        setOriginalDashboard(defaultDashboard);
      }
      setTagInput("");
      setSubInput("");
      setSmartInput("");
      setSmartParsing(false);
    }
  }, [open, task, defaultDashboard, defaultValues]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!form.tags.includes(t)) set("tags", [...form.tags, t]);
    setTagInput("");
  };

  const addSubtask = () => {
    const t = subInput.trim();
    if (!t) return;
    set("subtasks", [
      ...form.subtasks,
      { id: `${Date.now()}-${Math.random()}`, title: t, done: false },
    ]);
    setSubInput("");
  };

  const toggleSub = (i) => {
    const next = [...form.subtasks];
    next[i] = { ...next[i], done: !next[i].done };
    set("subtasks", next);
  };

  const removeSub = (i) => {
    const next = form.subtasks.filter((_, idx) => idx !== i);
    set("subtasks", next);
  };

  // ---- Smart input (natural-language) parser ----
  const applyParsed = (data) => {
    setForm((f) => ({
      ...f,
      title: data.title || f.title,
      due_date: data.due_date || f.due_date,
      due_time: data.due_time || f.due_time,
      due_times: data.due_times || (data.due_time ? [data.due_time] : f.due_times),
      start_time: data.start_time || f.start_time,
      end_time: data.end_time || f.end_time,
      priority: data.priority || f.priority,
      recurrence: data.recurrence
        ? { interval: 1, ...data.recurrence }
        : f.recurrence,
      recurring: data.recurrence
        ? legacyFromStructured({ interval: 1, ...data.recurrence })
        : f.recurring,
    }));
  };

  const runSmartParse = async () => {
    if (!smartInput.trim() || smartParsing) return;
    setSmartParsing(true);
    try {
      const { data } = await api.post("/tasks/parse", { text: smartInput.trim() });
      applyParsed(data);
      setSmartInput("");
      toast.success("Smart-filled from your phrase");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't parse — try rephrasing");
    } finally {
      setSmartParsing(false);
    }
  };

  const onVoiceParsed = ({ transcript, parsed }) => {
    if (parsed && Object.keys(parsed).length > 0) {
      applyParsed(parsed);
    } else if (transcript) {
      setForm((f) => ({ ...f, title: transcript.slice(0, 200) }));
    }
  };

  // ---- Reminder time management ----
  const reminderTimes = form.due_times?.length
    ? form.due_times
    : (form.due_time ? [form.due_time] : []);
  const setReminderTimes = (list) => {
    setForm((f) => ({
      ...f,
      due_times: list,
      due_time: list[0] || "",
    }));
  };
  const addReminderTime = () => {
    setReminderTimes([...(reminderTimes || []), ""]);
  };
  const updateReminderTime = (i, val) => {
    const next = [...reminderTimes];
    next[i] = val;
    setReminderTimes(next.filter((v, idx) => v || idx === 0));
  };
  const removeReminderTime = (i) => {
    setReminderTimes(reminderTimes.filter((_, idx) => idx !== i));
  };

  // ---- Location helpers ----
  // (PlaceAutocomplete handles search + "use my current location" inline.)
  const setLocationField = (k, v) => {
    setForm((f) => ({
      ...f,
      location: { ...(f.location || {}), [k]: v },
    }));
  };

  const clearLocation = () => {
    setForm((f) => ({ ...f, location: null }));
  };

  const hasLocation = !!form.location;
  const hasTimebox = !!(form.start_time || form.end_time);

  const ws = getWorkspace(form.dashboard);
  const WsIcon = ws.icon;
  const isMovingWorkspace = !!task && form.dashboard !== originalDashboard;
  const origWs = isMovingWorkspace ? getWorkspace(originalDashboard) : null;

  const onSave = async () => {
    if (!form.title.trim()) {
      toast.error("Task title is required");
      return;
    }
    if (isMovingWorkspace) {
      const confirmed = window.confirm(
        `Move this task from ${origWs.fullLabel} to ${ws.fullLabel}? It will no longer appear in ${origWs.fullLabel}.`
      );
      if (!confirmed) return;
    }
    setSaving(true);
    try {
      if (task) {
        await updateTask(task.id, form);
        toast.success(isMovingWorkspace ? `Task moved to ${ws.fullLabel}` : "Task updated");
      } else {
        const created = await createTask(form);
        // Flush any pending attachments queued before the task existed
        if (pendingAttachmentsRef.current?.length && created?.id) {
          await flushPendingAttachments(pendingAttachmentsRef.current, created.id);
          pendingAttachmentsRef.current = [];
        }
        toast.success(`Task added to ${ws.fullLabel}`);
      }
      onClose();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const friendly = formatApiError(detail);
      if (status === 402) {
        // Free-plan cap (or other plan-gated limit). Open the global upgrade
        // dialog so the user can decide between deleting tasks or upgrading.
        emitUpgradeRequired({
          title: "Task limit reached",
          message:
            friendly ||
            "Free accounts are capped at 50 tasks. Upgrade to Pro for unlimited tasks, or delete a few existing ones to make room.",
          used: 50,
          limit: 50,
          unit: "tasks",
        });
      } else if (status === 401 || status === 403) {
        toast.error("Session expired — please sign in again.");
      } else if (friendly) {
        toast.error(friendly);
      } else if (!navigator.onLine) {
        toast.error("You're offline — task will save when you reconnect.");
      } else {
        toast.error(`Failed to save task${status ? ` (HTTP ${status})` : ""}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!task) return;
    if (!window.confirm("Delete this task?")) return;
    try {
      await deleteTask(task.id);
      toast.success("Task deleted");
      onClose();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="task-creation-modal"
        className="max-w-2xl max-h-[90vh] overflow-y-auto p-0"
      >
        {/* Workspace identity banner */}
        <div
          data-testid="task-workspace-banner"
          className="flex items-center gap-3 px-6 pt-5 pb-4 border-b border-border transition-colors duration-300"
          style={{
            background: `linear-gradient(90deg, ${ws.color}14 0%, transparent 80%)`,
            borderBottomColor: `${ws.color}40`,
          }}
        >
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${ws.color}26`, color: ws.color }}
          >
            <WsIcon size={18} strokeWidth={2} />
          </div>
          <div className="leading-tight">
            <div
              className="text-[10px] uppercase tracking-[0.22em] font-bold"
              style={{ color: ws.color }}
            >
              {task ? "Editing task in" : "Adding task to"}
            </div>
            <div className="font-display font-bold text-base">{ws.fullLabel}</div>
          </div>
        </div>

        <div className="px-6 pt-5 pb-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="font-display text-2xl">
              {task ? "Edit Task" : "New Task"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {task ? "Edit task details" : `Create a new task in ${ws.fullLabel}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {/* Smart input — AI-powered natural language parser */}
            <div
              data-testid="smart-input-section"
              className="rounded-lg border border-dashed p-3 transition-colors"
              style={{
                borderColor: `${ws.color}50`,
                background: `linear-gradient(90deg, ${ws.color}08, transparent 80%)`,
              }}
            >
              <div className="flex items-center gap-1.5 mb-2">
                <Sparkles size={12} style={{ color: ws.color }} strokeWidth={2.2} />
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: ws.color }}>
                  Smart input
                </span>
                <span className="text-[10px] text-muted-foreground">— type or speak, AI fills the form</span>
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="smart-input"
                  value={smartInput}
                  onChange={(e) => setSmartInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), runSmartParse())}
                  placeholder='e.g. "Every weekday at 8am call mom"'
                  className="flex-1 h-9 text-sm"
                />
                <Button
                  type="button"
                  onClick={runSmartParse}
                  disabled={smartParsing || !smartInput.trim()}
                  data-testid="smart-input-btn"
                  className="h-9 px-3 transition-colors"
                  style={{ backgroundColor: ws.color, color: "#fff" }}
                >
                  {smartParsing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                </Button>
                <VoiceCapture
                  onParsed={onVoiceParsed}
                  accentColor={ws.color}
                  size="sm"
                  testIdPrefix="task-voice"
                />
              </div>
            </div>

            <div>
              <Input
                data-testid="task-title-input"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder="Task title…"
                className="text-lg font-display font-semibold h-12 border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary"
                style={{ caretColor: ws.color }}
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
              <Textarea
                data-testid="task-description-input"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Add details…"
                rows={3}
                className="mt-1.5"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Workspace</Label>
                <Select value={form.dashboard} onValueChange={(v) => set("dashboard", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="task-dashboard-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DASHBOARDS.map((d) => {
                      const w = getWorkspace(d.v);
                      const Icon = w.icon;
                      return (
                        <SelectItem key={d.v} value={d.v}>
                          <span className="inline-flex items-center gap-2">
                            <Icon size={13} style={{ color: w.color }} />
                            {d.l}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {isMovingWorkspace && (
                  <div
                    data-testid="workspace-move-warning"
                    className="mt-2 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
                    style={{
                      borderColor: `${ws.color}40`,
                      backgroundColor: `${ws.color}10`,
                      color: ws.color,
                    }}
                  >
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>
                      You're moving this task from <strong>{origWs.fullLabel}</strong> to{" "}
                      <strong>{ws.fullLabel}</strong>. You'll be asked to confirm before saving.
                    </span>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="task-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Repeat</Label>
                <RepeatPicker
                  value={form.recurrence}
                  onChange={(rule) =>
                    setForm((f) => ({
                      ...f,
                      recurrence: rule,
                      recurring: legacyFromStructured(rule),
                    }))
                  }
                  accentColor={ws.color}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Due date</Label>
                <Input
                  data-testid="task-duedate-input"
                  type="date"
                  value={form.due_date || ""}
                  onChange={(e) => set("due_date", e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <Bell size={11} strokeWidth={2} /> Reminder times
                  </Label>
                  <button
                    type="button"
                    onClick={addReminderTime}
                    className="text-[10px] uppercase tracking-wider text-primary font-bold hover:underline"
                    data-testid="add-reminder-time-btn"
                  >
                    + Add
                  </button>
                </div>
                <div className="mt-1.5 space-y-1.5">
                  {(reminderTimes.length === 0 ? [""] : reminderTimes).map((tval, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        data-testid={i === 0 ? "task-duetime-input" : `task-duetime-input-${i}`}
                        type="time"
                        value={tval || ""}
                        onChange={(e) => {
                          if (reminderTimes.length === 0) {
                            setReminderTimes([e.target.value]);
                          } else {
                            updateReminderTime(i, e.target.value);
                          }
                        }}
                        className="flex-1"
                      />
                      {reminderTimes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeReminderTime(i)}
                          className="p-1.5 rounded text-muted-foreground hover:text-destructive"
                          data-testid={`remove-reminder-time-${i}`}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Schedule preview — visible whenever the task has a due_date */}
            {form.due_date && (
              <SchedulePreview
                startDate={form.due_date}
                recurrence={form.recurrence}
                recurring={form.recurring}
                weeks={8}
                accentColor={ws.color}
              />
            )}

            {/* Timebox section — optional start + end time */}
            <div
              className={cn(
                "rounded-lg border px-3 py-3 transition-colors",
                hasTimebox
                  ? "border-primary/50 bg-primary/[0.04]"
                  : "border-border bg-secondary/20"
              )}
            >
              <SectionToggle
                icon={<Clock size={14} strokeWidth={2} />}
                label="Schedule a time block"
                checked={hasTimebox}
                onToggle={() =>
                  hasTimebox
                    ? setForm((f) => ({ ...f, start_time: "", end_time: "" }))
                    : setForm((f) => ({ ...f, start_time: "09:00", end_time: "10:00" }))
                }
                accent={ws.color}
                testId="timebox-toggle"
                ariaLabel="Toggle time block"
              />
              {hasTimebox && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Start</Label>
                    <Input
                      data-testid="task-start-time-input"
                      type="time"
                      value={form.start_time || ""}
                      onChange={(e) => set("start_time", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">End</Label>
                    <Input
                      data-testid="task-end-time-input"
                      type="time"
                      value={form.end_time || ""}
                      onChange={(e) => set("end_time", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Location reminder section */}
            <div
              className={cn(
                "rounded-lg border px-3 py-3 transition-colors",
                hasLocation
                  ? "border-primary/50 bg-primary/[0.04]"
                  : "border-border bg-secondary/20"
              )}
            >
              <SectionToggle
                icon={<MapPin size={14} strokeWidth={2} />}
                label="Add a location"
                checked={hasLocation}
                onToggle={() =>
                  hasLocation
                    ? clearLocation()
                    : setForm((f) => ({ ...f, location: { name: "", radius_meters: 200 } }))
                }
                accent={ws.color}
                testId="location-toggle"
                ariaLabel="Toggle location reminder"
              />
              {hasLocation && (
                <div className="space-y-3 mt-3">
                  <PlaceAutocomplete
                    value={form.location}
                    onChange={(next) => setForm((f) => ({ ...f, location: next }))}
                    accentColor={ws.color}
                    autoFocus={!form.location?.name}
                    testIdPrefix="task-location"
                  />
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Radius (meters): {form.location?.radius_meters || 200}m
                    </Label>
                    <input
                      data-testid="task-location-radius"
                      type="range"
                      min={50}
                      max={2000}
                      step={50}
                      value={form.location?.radius_meters || 200}
                      onChange={(e) => setLocationField("radius_meters", parseInt(e.target.value, 10))}
                      className="w-full mt-2"
                      style={{ accentColor: ws.color }}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div
                      data-testid="task-location-arrival-toggle"
                      className={cn(
                        "rounded-md border px-3 py-2 transition-colors",
                        form.location?.notify_on_arrival !== false
                          ? "border-primary/50 bg-primary/[0.04]"
                          : "border-border bg-secondary/30"
                      )}
                    >
                      <SectionToggle
                        icon={<LogIn size={13} strokeWidth={2} />}
                        label="Notify on arrival"
                        checked={form.location?.notify_on_arrival !== false}
                        onToggle={() =>
                          setLocationField(
                            "notify_on_arrival",
                            form.location?.notify_on_arrival === false
                          )
                        }
                        accent={ws.color}
                        testId="task-location-arrival-checkbox"
                        ariaLabel="Notify on arrival"
                      />
                      <p className="text-[10px] text-muted-foreground mt-1.5 ml-9">
                        When you arrive at this place
                      </p>
                    </div>
                    <div
                      data-testid="task-location-departure-toggle"
                      className={cn(
                        "rounded-md border px-3 py-2 transition-colors",
                        form.location?.notify_on_departure
                          ? "border-primary/50 bg-primary/[0.04]"
                          : "border-border bg-secondary/30"
                      )}
                    >
                      <SectionToggle
                        icon={<LogOut size={13} strokeWidth={2} />}
                        label="Notify on departure"
                        checked={!!form.location?.notify_on_departure}
                        onToggle={() =>
                          setLocationField(
                            "notify_on_departure",
                            !form.location?.notify_on_departure
                          )
                        }
                        accent={ws.color}
                        testId="task-location-departure-checkbox"
                        ariaLabel="Notify on departure"
                      />
                      <p className="text-[10px] text-muted-foreground mt-1.5 ml-9">
                        When you leave this place
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Subtasks */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Subtasks</Label>
              <div className="mt-1.5 space-y-2">
                {form.subtasks.map((s, i) => (
                  <div key={s.id || i} className="flex items-center gap-2 bg-secondary/40 rounded-md px-2 py-1.5">
                    <Checkbox checked={s.done} onCheckedChange={() => toggleSub(i)} data-testid={`subtask-check-${i}`} />
                    <span className={`flex-1 text-sm ${s.done ? "line-through text-muted-foreground" : ""}`}>{s.title}</span>
                    <button onClick={() => removeSub(i)} className="text-muted-foreground hover:text-destructive" data-testid={`subtask-remove-${i}`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <Input
                    value={subInput}
                    onChange={(e) => setSubInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSubtask())}
                    placeholder="Add a subtask…"
                    data-testid="subtask-input"
                  />
                  <Button type="button" variant="secondary" onClick={addSubtask} data-testid="subtask-add-btn">
                    <Plus size={14} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Attachments */}
            <TaskAttachments
              taskId={task?.id || null}
              pendingFilesRef={pendingAttachmentsRef}
              accentColor={ws.color}
            />

            {/* Tags */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tags / Category</Label>
              <div className="mt-1.5 flex flex-wrap gap-2 mb-2">
                {form.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-secondary">
                    {t}
                    <button onClick={() => set("tags", form.tags.filter((x) => x !== t))}>
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                  placeholder="Add tag and press Enter"
                  data-testid="tag-input"
                />
                <Button type="button" variant="secondary" onClick={addTag} data-testid="tag-add-btn">
                  <Plus size={14} />
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
              <Textarea
                data-testid="task-notes-input"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={2}
                className="mt-1.5"
              />
            </div>
          </div>

          <DialogFooter className="mt-5 flex sm:justify-between gap-2">
            {task ? (
              <Button
                type="button"
                variant="ghost"
                onClick={onDelete}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                data-testid="task-delete-btn"
              >
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} data-testid="task-cancel-btn">
                Cancel
              </Button>
              <Button
                type="button"
                onClick={onSave}
                disabled={saving}
                data-testid="task-save-btn"
                style={{ backgroundColor: ws.color, color: "#fff" }}
                className="hover:opacity-90 transition-opacity"
              >
                {saving ? "Saving…" : task ? "Save changes" : `Add to ${ws.label}`}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
